import React from 'react';
import { CheckIcon, MinusIcon, XIcon } from 'lucide-react';
import type { Density, DisclosureField } from '../../types/disclosure';
interface ValueProps {
  field: DisclosureField;
}
function FieldValue({
  field
}: ValueProps) {
  const {
    value,
    type
  } = field;
  if (value === null || value === '') {
    return <span className="inline-flex items-center text-slate-300" data-id="element-9" data-mp="9shcz90y8oyc">
        <MinusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-10" data-mp="3raaboji17ajx" />
        <span className="sr-only" data-id="element-11" data-mp="3r8pgcrnw7nl0">No value</span>
      </span>;
  }
  if (type === 'yesno') {
    const isYes = value === 'Yes';
    return <span className={['inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset', isYes ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-50 text-slate-600 ring-slate-200'].join(' ')} data-id="element-12" data-mp="3i9vn9b9rtfc7">
        {isYes ? <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-13" data-mp="3d8xxaciirh05" /> : <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-14" data-mp="29fn9dky4if7c" />}
        {value}
      </span>;
  }
  if (type === 'certified') {
    return <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200" data-id="element-15" data-mp="24jg13li98rm8">
        <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-16" data-mp="2dj36sji0ojl3" />
        {value}
      </span>;
  }
  return <span className="tabular-nums" data-id="element-17" data-mp="1pt8ty1xp6v0u">{value}</span>;
}
const COLUMN_CLASS: Record<1 | 2 | 3, string> = {
  1: '',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-2 xl:grid-cols-3'
};
interface FieldListProps {
  fields: DisclosureField[];
  density: Density;
  showApiNames: boolean;
  columns?: 1 | 2 | 3;
}
export function FieldList({
  fields,
  density,
  showApiNames,
  columns = 1
}: FieldListProps) {
  const rowPadding = density === 'compact' ? 'py-1.5' : 'py-2';
  const textSize = density === 'compact' ? 'text-[13px]' : 'text-sm';
  return <dl className={`grid gap-x-8 ${COLUMN_CLASS[columns]}`} data-id="element-18" data-mp="120sabmybauzf">
      {fields.map(field => <div key={`${field.api}-${field.label}`} className={`grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-baseline gap-3 border-b border-slate-100 ${rowPadding}`} data-id="element-19" data-mp="32308rjbz43pz">
          <dt className={`${textSize} leading-5 text-slate-500`} data-id="element-20" data-mp="3bx0jq1txs36m">
            {field.label}
            {showApiNames && <span className="mt-0.5 block truncate font-mono text-[10px] leading-4 text-slate-400" data-id="element-21" data-mp="2j6i7obb8irh2">
                {field.object ? `${field.object}.` : ''}
                {field.api}
              </span>}
          </dt>
          <dd className={`${textSize} leading-5 font-medium text-slate-900`} data-id="element-22" data-mp="2t3d3gmg4idrn">
            <FieldValue field={field} data-id="element-23" data-mp="2x6jahwb288z6" />
            {field.note && <span className="mt-0.5 block text-[11px] font-normal leading-4 text-slate-400" data-id="element-24" data-mp="2x3orm97p2w7v">
                {field.note}
              </span>}
          </dd>
        </div>)}
    </dl>;
}